import { createHTTPError, Logger } from '@gitbook/util-debug';

import type { DataSyncEncoder } from './createDataSyncEncoder';
import { requireRPCError, requireRPCReplyMessage } from './messages';
import * as protocol from './protocol';

export const DataSyncRPCServices = {
    DocumentCompletions: protocol.DocumentCompletions,
    Markers: protocol.Markers,
    Connection: protocol.Connection,
};

export type DataSyncRPCServiceName = keyof typeof DataSyncRPCServices;

export type DataSyncRPCService<Name extends DataSyncRPCServiceName> = InstanceType<
    (typeof DataSyncRPCServices)[Name]
>;

const logger = Logger('hive:datasync:rpc');

export interface DataSyncRPCClient<Name extends DataSyncRPCServiceName> {
    /**
     * Callback when a message is received from the server.
     * Returns true if the message was handled.
     */
    onReplyMessage: (message: protocol.IRPCReplyMessage) => boolean;

    /**
     * Service with typed methods.
     */
    service: DataSyncRPCService<Name>;
}

/**
 * Create a DataSync RPC client.
 */
export function createDataSyncRPCClient<Name extends DataSyncRPCServiceName>(
    serviceName: Name,
    encoder: DataSyncEncoder,
    options: {
        timeout?: number;
        subscriptionId?: () => number | undefined;
    } = {}
): DataSyncRPCClient<Name> {
    const {
        // Set a default timeout of 3 minutes
        timeout = 3 * 60 * 1000,
        subscriptionId,
    } = options;

    const pendingRequests = new Map<
        number,
        (error: Error | null, message?: Uint8Array | null) => void
    >();

    const service = new DataSyncRPCServices[serviceName](
        (method, requestData, callback) => {
            const messageId = encoder.newMessageID();
            const rpcRequest: protocol.IRPCRequestMessage = {
                id: messageId,
                service: serviceName,
                method: method.name,
                payload: requestData,
                subscriptionId: subscriptionId?.(),
            };

            // Stream methods starts by "stream" to identify them
            // it's a convention in GitBook not in RPC.
            const isStream = method.name.startsWith('stream');

            let ended = false;

            const timeoutId = setTimeout(() => {
                logger.warning(
                    `RPC request ${serviceName}.${method.name} timed out (${timeout / 1000}s)`
                );
                callback(new Error(`RPC request ${serviceName}.${method.name} timed out`));
                end();
            }, timeout);

            function end() {
                ended = true;
                pendingRequests.delete(messageId);
                clearTimeout(timeoutId);
            }

            const pendingCallback = (error: Error | null, message?: Uint8Array | null) => {
                // Already ended, for example because of a timeout
                if (ended) {
                    return;
                }

                // An error occured, we need to end the stream
                if (error) {
                    callback(error);
                    end();
                    return;
                }

                // It's a stream, we don't end it until we receive a null message
                if (isStream && message !== null) {
                    callback(null, message);
                    return;
                }

                // A non-stream method, we end the stream
                // or a stream method with a null message, we end the stream
                callback(null, message);
                end();
            };

            pendingRequests.set(messageId, pendingCallback);

            logger.info(`sending RPC request ${serviceName}.${method.name}: ${messageId}`);
            encoder.write({ rpcRequest });
        },
        false,
        false
    );

    return {
        onReplyMessage: (message) => {
            const rpcReply = requireRPCReplyMessage(message);
            const callback = pendingRequests.get(rpcReply.replyTo);
            if (!callback) {
                return false;
            }

            logger.info(`received RPC reply for ${rpcReply.replyTo}`);
            if (rpcReply.error) {
                const rpcError = requireRPCError(rpcReply.error);
                const error = createHTTPError.fromStatusCode(rpcError.code, rpcError.message);
                callback(error);
                return true;
            }

            callback(null, rpcReply.payload ?? null);
            return true;
        },
        // @ts-ignore
        service,
    };
}
